import axios from "axios";
import { Package, Search, Filter, X, Plus, Minus, ShoppingCart, Trash2, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { AnimatePresence, motion } from "framer-motion";

const ITEMS_PER_PAGE = 20;

const StaffAllProducts = () => {
  const { admin } = useAdminAuth();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsageType, setSelectedUsageType] = useState("all");
  const [showUsageFilter, setShowUsageFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [cart, setCart] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const cartItemsCount = cart.reduce((sum, item) => sum + item.requestedQuantity, 0);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedCategory, selectedUsageType, products]);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/products/get-all`);
      const data = res?.data?.data || [];
      setProducts(data);
      setFilteredProducts(data);
    } catch (err) {
      console.error("Fetch products failed", err);
      showError("Failed to load products");
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    if (selectedUsageType !== "all") {
      filtered = filtered.filter(p => p.productUsageType === selectedUsageType);
    }

    if (searchQuery.trim()) {
      const lower = searchQuery.toLowerCase();
      filtered = filtered.filter(
        p =>
          p.productName?.toLowerCase().includes(lower) ||
          p.brand?.toLowerCase().includes(lower)
      );
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product._id === product._id);

    if (existingItem) {
      setCart(cart.map(item =>
        item.product._id === product._id
          ? { ...item, requestedQuantity: item.requestedQuantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { product, requestedQuantity: 1 }]);
    }

    showSuccess(`${product.productName} added to cart`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev =>
      prev
        .map(item => {
          if (item.product._id === productId) {
            const qty = item.requestedQuantity + delta;
            return qty > 0 ? { ...item, requestedQuantity: qty } : null;
          }
          return item;
        })
        .filter(Boolean)
    );
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product._id !== productId));
  };

  const clearCart = () => {
    setCart([]);
  };

  const submitRequest = async () => {
    if (cart.length === 0) {
      showError("Please add at least one product to request");
      return;
    }

    // Get staff and branch info from admin context
    if (!admin?.id || !admin?.assignedBranch) {
      showError("Staff or branch information missing. Please contact administrator.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        branch: admin.assignedBranch,
        staff: admin.id,
        products: cart.map(item => ({
          product: item.product._id,
          requestedQuantity: item.requestedQuantity,
        })),
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/product-requests/create`,
        payload
      );

      showSuccess("Product request submitted successfully");
      clearCart();
      setShowCartModal(false);
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to submit request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeFiltersCount =
    (selectedCategory !== "all" ? 1 : 0) +
    (selectedUsageType !== "all" ? 1 : 0) +
    (searchQuery.trim() ? 1 : 0);

  return (
    <div className="min-h-screen bg-gray-50 no-scrollbar">
      {/* Header - Modern gradient style matching MyJobs */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-primaryColor/10 to-accentTextColor/10 backdrop-blur-md border-b border-primaryColor/20 shadow-sm">
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            {/* Left */}
            <div className="leading-tight">
              <h1 className="text-base md:text-xl font-bold text-primaryTextColor">
                All Products
              </h1>
              <p className="text-xs md:text-sm text-secondaryTextColor mt-0.5">
                Browse and request products
              </p>
            </div>

            {/* Right - Cart Button */}
            <button
              onClick={() => setShowCartModal(true)}
              className="relative flex items-center gap-2 px-3 py-2 md:px-4 md:py-2.5 bg-white hover:bg-gray-50 active:scale-95 rounded-full shadow-md border border-gray-200 transition"
            >
              <ShoppingCart size={18} className="md:w-5 md:h-5 text-gray-700" />
              {cart.length > 0 && (
                <>
                  <span className="hidden sm:inline text-sm font-medium text-gray-700">
                    Cart ({cart.length})
                  </span>
                  {/* Badge */}
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {cart.length}
                  </div>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 sm:py-6 md:py-8 pb-20">
        <div className="mx-auto max-w-7xl">
          <div className="bg-white rounded-lg md:rounded-md shadow-md md:shadow-lg p-3 md:p-6">
            {/* DESKTOP LAYOUT */}
            <div className="hidden md:block">
              <div className="flex flex-col gap-4 mb-6">
                {/* Top Row: Filters */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    {/* Search Bar */}
                    <div className="relative w-full max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search products..."
                        className="w-full shadow-md pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor transition"
                      />
                    </div>
                    {/* Usage Type Filter */}
                    <div className="relative">
                      <button
                        onClick={() => setShowUsageFilter(!showUsageFilter)}
                        className={`px-4 py-2.5 border shadow-md rounded-lg font-medium flex items-center gap-2 transition ${selectedUsageType !== "all"
                          ? "bg-green-50 border-green-300 text-green-700"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        <Filter size={18} />
                        Usage Type
                        {selectedUsageType !== "all" && (
                          <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">1</span>
                        )}
                      </button>

                      {showUsageFilter && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowUsageFilter(false)} />
                          <div className="absolute top-full mt-2 left-0 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-20">
                            <div className="p-2 space-y-1">
                              {[
                                { label: "All", value: "all" },
                                { label: "Reusable", value: "reusable" },
                                { label: "Use & Throw", value: "use_and_throw" },
                              ].map((item) => (
                                <button
                                  key={item.value}
                                  onClick={() => {
                                    setSelectedUsageType(item.value);
                                    setShowUsageFilter(false);
                                  }}
                                  className={`w-full text-left px-4 py-2 rounded-lg transition ${selectedUsageType === item.value
                                    ? "bg-green-50 text-green-700 font-medium"
                                    : "hover:bg-gray-100"
                                    }`}
                                >
                                  {item.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Clear Filters */}
                    {activeFiltersCount > 0 && (
                      <button
                        onClick={() => {
                          setSearchQuery("");
                          setSelectedCategory("all");
                          setSelectedUsageType("all");
                        }}
                        className="px-3 py-2.5 text-gray-600 hover:text-gray-900 font-medium flex items-center gap-2 transition border border-gray-300 rounded-lg bg-white"
                      >
                        <X size={18} />
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                {/* Results Summary */}
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>
                      Showing <span className="font-medium text-gray-900">{filteredProducts.length}</span> of{" "}
                      <span className="font-medium text-gray-900">{products.length}</span> products
                    </span>
                    {selectedCategory !== "all" && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {selectedCategory}
                      </span>
                    )}
                    {selectedUsageType !== "all" && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        {selectedUsageType === "use_and_throw" ? "Use & Throw" : "Reusable"}
                      </span>
                    )}
                  </div>

                  {activeFiltersCount > 0 && (
                    <div className="text-blue-600">
                      {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} active
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* MOBILE LAYOUT */}
            <div className="md:hidden">
              <div className="space-y-3 mb-4">
                {/* Search Bar - Full width */}
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    className="w-full shadow-md pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor transition"
                  />
                </div>

                {/* Filters Row */}
                <div className="flex items-center gap-2">

                  {/* Usage Type Filter */}
                  <div className="relative flex-1">
                    <button
                      onClick={() => setShowUsageFilter(!showUsageFilter)}
                      className={`w-full px-3 py-2 border shadow-md rounded-lg font-medium flex items-center justify-center gap-2 transition text-sm ${selectedUsageType !== "all"
                        ? "bg-green-50 border-green-300 text-green-700"
                        : "bg-white border-gray-300 text-gray-700"
                        }`}
                    >
                      <Filter size={16} />
                      <span>Type</span>
                      {selectedUsageType !== "all" && (
                        <span className="px-1.5 py-0.5 bg-green-500 text-white text-xs rounded-full">1</span>
                      )}
                    </button>

                    {showUsageFilter && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowUsageFilter(false)} />
                        <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-lg shadow-xl border border-gray-200 z-20">
                          <div className="p-2 space-y-1">
                            {[
                              { label: "All", value: "all" },
                              { label: "Reusable", value: "reusable" },
                              { label: "Use & Throw", value: "use_and_throw" },
                            ].map((item) => (
                              <button
                                key={item.value}
                                onClick={() => {
                                  setSelectedUsageType(item.value);
                                  setShowUsageFilter(false);
                                }}
                                className={`w-full text-left px-4 py-2 rounded-lg transition text-sm ${selectedUsageType === item.value
                                  ? "bg-green-50 text-green-700 font-medium"
                                  : "hover:bg-gray-100"
                                  }`}
                              >
                                {item.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Clear Filters */}
                  {activeFiltersCount > 0 && (
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setSelectedCategory("all");
                        setSelectedUsageType("all");
                      }}
                      className="px-2 py-2 text-gray-600 font-medium flex items-center gap-1 transition text-sm border border-gray-300 rounded-lg bg-white"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {/* Mobile Results Summary */}
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <span>
                    <span className="font-medium text-gray-900">{filteredProducts.length}</span> of{" "}
                    <span className="font-medium text-gray-900">{products.length}</span>
                  </span>
                  {activeFiltersCount > 0 && (
                    <span className="text-blue-600">{activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Products Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-200 h-16 md:h-20 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12 md:py-16">
                <Package size={48} className="md:w-16 md:h-16 mx-auto text-gray-300 mb-3 md:mb-4" />
                <p className="text-gray-500 text-sm md:text-base">No products found</p>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedCategory("all");
                      setSelectedUsageType("all");
                    }}
                    className="mt-3 px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-3">
                  {paginatedProducts.map((product) => {
                    const cartItem = cart.find(item => item.product._id === product._id);

                    return (
                      <div
                        key={product._id}
                        className="bg-white border rounded-lg p-2 md:p-3 flex items-center justify-between gap-2 hover:shadow-md transition-shadow"
                      >
                        {/* Product Info */}
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-sm capitalize md:text-sm text-gray-800 truncate">
                            {product.productName}
                          </h3>
                          <div className="flex gap-1 items-center text-[10px] md:text-xs text-gray-500">
                            <p className="truncate capitalize">{product.brand}</p>
                          </div>
                          <span
                            className={`inline-block mt-1 text-[9px] md:text-[10px] ${product.productUsageType === "reusable"
                              ? "text-green-800 bg-green-100"
                              : "text-orange-800 bg-orange-100"
                              } py-0.5 px-1.5 rounded-full font-medium`}
                          >
                            {product.productUsageType === "reusable" ? "Returnable" : "Use & throw"}
                          </span>
                        </div>

                        {/* Action */}
                        {!cartItem ? (
                          <button
                            onClick={() => addToCart(product)}
                            className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full flex-shrink-0 bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor active:scale-95 transition"
                          >
                            <Plus size={16} className="md:w-[18px] md:h-[18px]" />
                          </button>
                        ) : (
                          <div className="flex items-center gap-1 flex-shrink-0 bg-blue-50 rounded-full px-1 py-1">
                            <button
                              onClick={() => updateQuantity(product._id, -1)}
                              className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center bg-white rounded-full hover:bg-gray-50 active:scale-95 transition shadow-sm"
                            >
                              <Minus size={12} className="md:w-[14px] md:h-[14px]" />
                            </button>

                            <span className="w-6 md:w-7 text-center text-xs md:text-sm font-bold text-blue-700">
                              {cartItem.requestedQuantity}
                            </span>

                            <button
                              onClick={() => updateQuantity(product._id, 1)}
                              className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center bg-white rounded-full hover:bg-gray-50 active:scale-95 transition shadow-sm"
                            >
                              <Plus size={12} className="md:w-[14px] md:h-[14px]" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4 md:mt-6">
                    <p className="text-xs md:text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </p>

                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                        className="flex-1 sm:flex-none px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition text-sm"
                      >
                        Prev
                      </button>
                      <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                        className="flex-1 sm:flex-none px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition text-sm"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cart Modal */}
      <CartModal
        isOpen={showCartModal}
        cart={cart}
        onClose={() => setShowCartModal(false)}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        clearCart={clearCart}
        submitRequest={submitRequest}
        isSubmitting={isSubmitting}
        cartItemsCount={cartItemsCount}
      />
    </div>
  );
};

// Cart Modal Component
const CartModal = ({
  isOpen,
  cart,
  onClose,
  updateQuantity,
  removeFromCart,
  clearCart,
  submitRequest,
  isSubmitting,
  cartItemsCount
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Mobile: Bottom Sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col md:hidden"
          >
            {/* Handle */}
            <div className="flex justify-center py-2 bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryColor">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            </div>

            <CartContent
              cart={cart}
              onClose={onClose}
              updateQuantity={updateQuantity}
              removeFromCart={removeFromCart}
              clearCart={clearCart}
              submitRequest={submitRequest}
              isSubmitting={isSubmitting}
              cartItemsCount={cartItemsCount}
            />
          </motion.div>

          {/* Desktop: Centered Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="hidden md:flex fixed inset-0 items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <CartContent
                cart={cart}
                onClose={onClose}
                updateQuantity={updateQuantity}
                removeFromCart={removeFromCart}
                clearCart={clearCart}
                submitRequest={submitRequest}
                isSubmitting={isSubmitting}
                cartItemsCount={cartItemsCount}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Cart Content Component
const CartContent = ({
  cart,
  onClose,
  updateQuantity,
  removeFromCart,
  clearCart,
  submitRequest,
  isSubmitting,
  cartItemsCount
}) => {
  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-6 bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryColor py-4 border-b">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-brandAccentColor rounded-full flex items-center justify-center">
            <ShoppingCart size={20} className="text-primaryColor" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-bold text-white">Your Cart</h2>
            <p className="text-xs md:text-sm text-white">
              {cart.length} product{cart.length !== 1 ? 's' : ''} · {cartItemsCount} item{cartItemsCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-8 h-8 bg-brandAccentColor hover:bg-gray-200 rounded-full flex items-center justify-center transition active:scale-95"
        >
          <X size={17} className="text-white" />
        </button>
      </div>

      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <ShoppingCart size={32} className="md:w-10 md:h-10 text-gray-400" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Your cart is empty</h3>
            <p className="text-sm text-gray-500 mb-6">Add products to your cart to submit a request</p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-primaryBtnColor text-white font-medium rounded-lg hover:bg-primaryBtnHoverColor active:scale-95 transition"
            >
              Continue Adding
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {cart.map((item) => (
              <div
                key={item.product._id}
                className="bg-gray-50 rounded-xl p-3 md:p-4 flex gap-3"
              >
                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm md:text-base text-gray-900 line-clamp-1">
                    {item.product.productName}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 mt-0.5">{item.product.brand}</p>

                  <div className="flex items-center gap-2 mt-2">
                    <span
                      className={`text-[9px] md:text-[10px] px-2 py-0.5 rounded-full font-medium ${item.product.productUsageType === "reusable"
                        ? "bg-green-100 text-green-700"
                        : "bg-orange-100 text-orange-700"
                        }`}
                    >
                      {item.product.productUsageType === "reusable" ? "Returnable" : "Use & throw"}
                    </span>
                  </div>
                </div>

                {/* Quantity Controls */}
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-1.5 bg-white rounded-full px-1 py-1 shadow-sm">
                    <button
                      onClick={() => updateQuantity(item.product._id, -1)}
                      className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition"
                    >
                      <Minus size={14} className="md:w-4 md:h-4" />
                    </button>

                    <span className="w-8 md:w-10 text-center text-sm md:text-base font-bold text-gray-900">
                      {item.requestedQuantity}
                    </span>

                    <button
                      onClick={() => updateQuantity(item.product._id, 1)}
                      className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition"
                    >
                      <Plus size={14} className="md:w-4 md:h-4" />
                    </button>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeFromCart(item.product._id)}
                    className="text-red-600 hover:text-red-700 text-xs md:text-sm font-medium flex items-center gap-1 active:scale-95 transition"
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {cart.length > 0 && (
        <div className="border-t px-4 md:px-6 py-4 space-y-3">
          {/* Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Total Items:</span>
            <span className="font-bold text-gray-900">{cartItemsCount}</span>
          </div>

          {/* Buttons */}
          <div className="flex gap-2">
            <button
              onClick={clearCart}
              className="flex-1 px-3 md:px-4 py-3 text-sm md:text-base md:py-3.5 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 active:scale-[0.98] transition"
            >
              Clear Cart
            </button>
            <button
              onClick={submitRequest}
              disabled={isSubmitting}
              className="flex-1 px-3 md:px-4 py-3 md:py-3.5 bg-primaryBtnColor text-white text-sm md:text-base font-semibold rounded-xl hover:bg-primaryBtnHoverColor active:scale-[0.98] transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Request
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default StaffAllProducts;